import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Avatar,
  Button,
  Typography,
  Row,
  Col,
  Descriptions,
  Tag,
  Divider,
  Space,
  ConfigProvider,
  Spin,
  message,
  Upload
} from 'antd';
import {
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  BankOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  LoadingOutlined,
  CameraOutlined
} from '@ant-design/icons';
import investorApi from '../../api/investorApi';

const { Title, Text, Link } = Typography;
const { Content } = Layout;

const InvestorProfile = () => {
  const brandColor = '#3C007A';
  const [loading, setLoading] = useState(true);
  const [investorData, setInvestorData] = useState(null);
  const [logoError, setLogoError] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const defaultLogo = 'https://ui-avatars.com/api/?name=Investor&background=3C007A&color=fff&size=128&bold=true';

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    const normalizedPath = imagePath.replace(/\\/g, '/').replace(/^\/+/, '');
    if (normalizedPath.startsWith('uploads/')) {
      return `${API_URL}/${normalizedPath}`;
    }
    return `${API_URL}/uploads/${normalizedPath}`;
  };

  useEffect(() => {
    fetchInvestorProfile();
  }, []);

  const fetchInvestorProfile = async () => {
    try {
      setLoading(true);
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

      if (userInfo.investor) {
        const data = await investorApi.getInvestorProfile(userInfo.investor);
        setInvestorData(data);
      } else {
        message.error('No investor ID found. Please login again.');
      }
    } catch (error) {
      message.error('Failed to load investor profile');
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (file) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const formData = new FormData();
      const fieldName = investorData?.investorType === 'company' ? 'companyLogo' : 'profilePicture';
      formData.append(fieldName, file);

      await investorApi.updateInvestorProfile(userInfo.investor, formData);
      message.success('Photo uploaded successfully');
      fetchInvestorProfile();
      return false;
    } catch (error) {
      message.error('Failed to upload photo');
      return false;
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error'
    };
    return statusMap[status?.toLowerCase()] || 'default';
  };

  const getStatusText = (status) => {
    const statusTextMap = {
      approved: 'Verified',
      pending: 'Pending',
      rejected: 'Rejected'
    };
    return statusTextMap[status?.toLowerCase()] || status || 'Pending';
  };

  const isCompany = investorData?.investorType === 'company';
  const displayName = isCompany ? investorData?.companyName : investorData?.fullName;
  const rawLogoPath = isCompany ? (investorData?.companyLogo || investorData?.logo) : investorData?.profilePicture;
  const logoUrl = rawLogoPath ? getImageUrl(rawLogoPath) : defaultLogo;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f0f2f5' }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: brandColor }} spin />} />
      </div>
    );
  }

  if (!investorData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#f0f2f5' }}>
        <Text type="secondary">No investor data available. Please contact support.</Text>
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: brandColor,
          fontFamily: "'Rubik', sans-serif",
          borderRadius: 6,
          fontSize: 16,
        },
      }}
    >
      <div style={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        position: 'relative',
        overflowX: 'hidden',
        fontFamily: "'Rubik', sans-serif"
      }}>
        {/* Header banner */}
        <div style={{
          height: 'clamp(160px, 24vh, 260px)',
          background: `linear-gradient(135deg, ${brandColor}, #7b2ff7)`,
          position: 'relative',
          width: '100%',
          maxWidth: '100%'
        }} />

        {/* Content Container */}
        <div style={{
          padding: '0 24px 24px',
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          boxSizing: 'border-box'
        }}>
          {/* Header Card */}
          <Card
            bordered={false}
            style={{
              marginTop: -60,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: '100%'
            }}
            bodyStyle={{ padding: '24px 32px' }}
          >
            <Row gutter={[24, 24]} align="middle">
              <Col xs={24} sm={6} md={4} style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <div style={{
                    width: 140,
                    height: 140,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {logoError || !rawLogoPath ? (
                      <Avatar
                        size={130}
                        style={{
                          backgroundColor: brandColor,
                          fontSize: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white'
                        }}
                        icon={isCompany ? <BankOutlined /> : <UserOutlined />}
                      >
                        {displayName?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    ) : (
                      <img
                        src={logoUrl}
                        alt="Investor"
                        style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '8px' }}
                        onError={() => setLogoError(true)}
                      />
                    )}
                  </div>

                  <Upload showUploadList={false} beforeUpload={handleLogoUpload} accept="image/*">
                    <Button
                      icon={<CameraOutlined />}
                      shape="circle"
                      style={{
                        position: 'absolute',
                        bottom: 5,
                        right: 5,
                        zIndex: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </Upload>
                </div>
              </Col>

              <Col xs={24} sm={18} md={20}>
                <Space direction="vertical" size={8}>
                  <Title
                    level={2}
                    style={{
                      margin: 0,
                      color: brandColor,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 500,
                      fontSize: '32px',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    {displayName || 'Investor'}
                  </Title>
                  <Space wrap>
                    <Tag color="purple" style={{ margin: 0, fontSize: '16px', padding: '4px 12px' }}>
                      {isCompany ? 'Company Investor' : 'Individual Investor'}
                    </Tag>
                    {investorData.status && (
                      <Tag
                        icon={investorData.status === 'approved' ? <SafetyCertificateOutlined /> : undefined}
                        color={getStatusColor(investorData.status)}
                        style={{ fontSize: '16px', padding: '4px 12px' }}
                      >
                        {getStatusText(investorData.status)}
                      </Tag>
                    )}
                  </Space>
                  {investorData.website && (
                    <Link
                      href={investorData.website}
                      target="_blank"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '16px', color: '#333' }}
                    >
                      <GlobalOutlined /> {investorData.website}
                    </Link>
                  )}
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Details Grid */}
          <Row gutter={[24, 24]} style={{ marginTop: 24, width: '100%', maxWidth: '100%' }}>
            <Col xs={24} lg={16}>
              <Card
                title={
                  <Space style={{ fontSize: '20px', fontWeight: 400, color: brandColor }}>
                    <DollarOutlined style={{ color: brandColor, fontSize: '20px' }} />
                    Investor Details
                  </Space>
                }
                bordered={false}
                style={{ height: '100%' }}
              >
                <Descriptions
                  column={{ xs: 1, sm: 2 }}
                  layout="vertical"
                  labelStyle={{ fontWeight: 400, color: '#666', fontSize: '16px', textAlign: 'center' }}
                  contentStyle={{ fontSize: '17px', fontWeight: 400, color: brandColor, wordBreak: 'break-word' }}
                >
                  {isCompany && (
                    <Descriptions.Item label="Company Name">
                      <Text strong style={{ fontSize: 18, color: brandColor }}>{investorData.companyName || 'N/A'}</Text>
                    </Descriptions.Item>
                  )}
                  {!isCompany && (
                    <Descriptions.Item label="Full Name">
                      <Text strong style={{ fontSize: 18, color: brandColor }}>{investorData.fullName || 'N/A'}</Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label={isCompany ? 'Contact Person' : 'Professional Title'}>
                    {isCompany ? (investorData.contactPerson || 'N/A') : (investorData.designation || 'Not Specified')}
                  </Descriptions.Item>
                  {isCompany && investorData.registrationNumber && (
                    <Descriptions.Item label="Registration Number">
                      {investorData.registrationNumber}
                    </Descriptions.Item>
                  )}
                  {isCompany && investorData.yearFounded && (
                    <Descriptions.Item label="Year Founded">
                      {investorData.yearFounded}
                    </Descriptions.Item>
                  )}
                  {investorData.ticketSize && (
                    <Descriptions.Item label="Ticket Size">
                      {investorData.ticketSize}
                    </Descriptions.Item>
                  )}
                  {investorData.investmentStage && (
                    <Descriptions.Item label="Investment Stage">
                      {investorData.investmentStage}
                    </Descriptions.Item>
                  )}
                  {investorData.country && (
                    <Descriptions.Item label="Country">
                      {investorData.country}
                    </Descriptions.Item>
                  )}
                  {investorData.city && (
                    <Descriptions.Item label="City">
                      {investorData.city}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>

              {investorData.about && (
                <Card
                  title={<Space style={{ fontSize: '20px', fontWeight: 400, color: brandColor }}>About</Space>}
                  bordered={false}
                  style={{ marginTop: 24 }}
                >
                  <Text style={{ display: 'block', lineHeight: 1.8, fontSize: '16px', color: '#333' }}>
                    {investorData.about}
                  </Text>
                </Card>
              )}
            </Col>

            <Col xs={24} lg={8}>
              <Card
                title={
                  <Space style={{ fontSize: '20px', fontWeight: 400, color: brandColor }}>
                    <EnvironmentOutlined style={{ color: brandColor }} />
                    Contact Information
                  </Space>
                }
                bordered={false}
                style={{ height: '100%', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                bodyStyle={{ padding: '24px' }}
              >
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                  <Divider style={{ margin: '3px 0' }} />

                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 48, height: 48, borderRadius: '12px', backgroundColor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <MailOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, color: '#666' }}>
                        Email Address
                      </Text>
                      <Text style={{ fontSize: 17, color: '#333', fontWeight: 500 }}>
                        {investorData.email || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 48, height: 48, borderRadius: '12px', backgroundColor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PhoneOutlined style={{ fontSize: '20px', color: brandColor }} />
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 4, color: '#666' }}>
                        Mobile Number
                      </Text>
                      <Text style={{ fontSize: 17, color: '#333', fontWeight: 500 }}>
                        {investorData.phone || 'N/A'}
                      </Text>
                    </div>
                  </div>

                  <Divider style={{ margin: '0px 0' }} />

                  <div style={{ textAlign: 'center', marginTop: 0 }}>
                    <Text type="secondary" style={{ fontSize: 18, marginBottom: 8, display: 'block' }}>
                      Address
                    </Text>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, alignItems: 'flex-start' }}>
                      <EnvironmentOutlined style={{ color: brandColor, marginTop: 4 }} />
                      <Text style={{ fontSize: 14, color: '#333', maxWidth: '80%', lineHeight: 1.5 }}>
                        {investorData.address || 'Not specified'}
                      </Text>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default InvestorProfile;
